import React, { useState, useEffect, useCallback } from 'react';
import { ShoppingBag, Calendar, Clock, ArrowRight, X } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';

// Formato venezolano: 60.325,60
const formatMoney = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '0,00';
    return num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const VentaEspecialWidget = () => {
    const [pedidoPendiente, setPedidoPendiente] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dismissed, setDismissed] = useState(false);
    const [countdown, setCountdown] = useState(null);
    const navigate = useNavigate();

    // Check dismiss state
    useEffect(() => {
        const today = new Date().toDateString();
        const key = `widget_dismiss_venta_${today}`;
        if (localStorage.getItem(key)) setDismissed(true);
    }, []);

    useEffect(() => {
        fetchMiPedido();
    }, []);

    const fetchMiPedido = async () => {
        try {
            const response = await axiosInstance.get('/api/ventas-especiales/activa');
            if (response.data.activa !== false && response.data.mi_pedido) {
                setPedidoPendiente({
                    venta: response.data,
                    pedido: response.data.mi_pedido
                });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // Live countdown timer
    const calculateCountdown = useCallback(() => {
        if (!pedidoPendiente?.venta?.fechaLimitePago) return null;
        const target = new Date(pedidoPendiente.venta.fechaLimitePago);
        const now = new Date();
        const diff = target - now;
        if (diff <= 0) return { expired: true };

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        return { days, hours, minutes, seconds, expired: false, totalMs: diff };
    }, [pedidoPendiente]);

    useEffect(() => {
        if (!pedidoPendiente) return;
        const update = () => setCountdown(calculateCountdown());
        update();
        const interval = setInterval(update, 1000);
        return () => clearInterval(interval);
    }, [pedidoPendiente, calculateCountdown]);

    const handleDismiss = () => {
        if (window.confirm('¿Deseas ocultar este widget por hoy?')) {
            const today = new Date().toDateString();
            localStorage.setItem(`widget_dismiss_venta_${today}`, 'true');
            setDismissed(true);
        }
    };

    if (loading || !pedidoPendiente || dismissed) return null;
    if (pedidoPendiente.pedido.estado === 'completado') return null;

    const { venta, pedido } = pedidoPendiente;
    const fechaLimite = venta.fechaLimitePago ? new Date(venta.fechaLimitePago) : null;
    const hoy = new Date();
    const diasRestantes = fechaLimite ? Math.ceil((fechaLimite - hoy) / (1000 * 60 * 60 * 24)) : null;

    // Determinar color según urgencia
    let urgencia = 'teal';
    if (diasRestantes !== null) {
        if (diasRestantes <= 2) urgencia = 'red';
        else if (diasRestantes <= 5) urgencia = 'orange';
    }

    const colores = {
        teal: {
            bg: 'from-teal-500 to-emerald-600',
            bgLight: 'bg-teal-50',
            text: 'text-teal-600',
            border: 'border-teal-200',
            ring: 'stroke-teal-500'
        },
        orange: {
            bg: 'from-orange-500 to-amber-600',
            bgLight: 'bg-orange-50',
            text: 'text-orange-600',
            border: 'border-orange-200',
            ring: 'stroke-orange-500'
        },
        red: {
            bg: 'from-red-500 to-rose-600',
            bgLight: 'bg-red-50',
            text: 'text-red-600',
            border: 'border-red-200',
            ring: 'stroke-red-500'
        }
    };

    const c = colores[urgencia];

    // Calculate ring progress (30 days max window)
    const totalDays = 30;
    const progress = diasRestantes !== null ? Math.max(0, Math.min(1, diasRestantes / totalDays)) : 1;
    const circumference = 2 * Math.PI * 40;
    const strokeDashoffset = circumference * (1 - progress);

    return (
        <section className={`relative rounded-2xl overflow-hidden border ${c.border} shadow-sm group`}>
            {/* Dismiss button */}
            <button
                onClick={handleDismiss}
                className="absolute top-3 right-3 z-20 w-6 h-6 rounded-full bg-white/80 backdrop-blur-sm text-gray-400 hover:text-gray-600 hover:bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-sm"
                title="Ocultar por hoy"
            >
                <X size={14} />
            </button>

            {/* Gradient header */}
            <div className={`bg-gradient-to-r ${c.bg} px-5 py-4 text-white`}>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <ShoppingBag size={20} />
                    </div>
                    <div>
                        <h3 className="font-bold text-lg">Venta Especial</h3>
                        <p className="text-white/80 text-sm">{venta.titulo}</p>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className={`${c.bgLight} p-5`}>
                {/* Total + Countdown Ring */}
                <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm mb-4">
                    <div className="flex items-center gap-4">
                        <div className="flex-1">
                            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Total a Pagar</p>
                            <p className="text-3xl font-bold text-gray-900 mt-1">
                                Bs {formatMoney(pedido.totalCalculado)}
                            </p>
                        </div>

                        {/* Countdown Ring */}
                        {countdown && !countdown.expired && (
                            <div className="relative w-20 h-20 shrink-0">
                                <svg className="w-20 h-20 -rotate-90" viewBox="0 0 100 100">
                                    <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" strokeWidth="6" />
                                    <circle
                                        cx="50" cy="50" r="40" fill="none"
                                        className={c.ring}
                                        strokeWidth="6"
                                        strokeLinecap="round"
                                        strokeDasharray={circumference}
                                        strokeDashoffset={strokeDashoffset}
                                        style={{ transition: 'stroke-dashoffset 1s ease-in-out' }}
                                    />
                                </svg>
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    {countdown.days > 0 ? (
                                        <>
                                            <span className={`text-xl font-bold ${c.text}`}>{countdown.days}</span>
                                            <span className="text-[10px] text-gray-500 font-medium">días</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className={`text-lg font-bold ${c.text} tabular-nums`}>
                                                {String(countdown.hours).padStart(2, '0')}:{String(countdown.minutes).padStart(2, '0')}
                                            </span>
                                            <span className="text-[10px] text-gray-500 font-medium tabular-nums">
                                                {String(countdown.seconds).padStart(2, '0')}s
                                            </span>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {fechaLimite && (
                    <div className="flex items-center gap-3 mb-4">
                        <div className={`w-10 h-10 rounded-lg ${c.bgLight} ${c.text} flex items-center justify-center`}>
                            <Calendar size={18} />
                        </div>
                        <div className="flex-1">
                            <p className="text-xs text-gray-500">Fecha límite de pago</p>
                            <p className="font-bold text-gray-900">
                                {fechaLimite.toLocaleDateString('es-VE', { day: 'numeric', month: 'long' })}
                            </p>
                        </div>
                        {diasRestantes !== null && diasRestantes >= 0 && (
                            <div className={`px-3 py-1.5 rounded-full ${c.bgLight} ${c.text} text-sm font-bold flex items-center gap-1`}>
                                <Clock size={14} />
                                {diasRestantes === 0 ? 'Hoy' : diasRestantes === 1 ? '1 día' : `${diasRestantes} días`}
                            </div>
                        )}
                    </div>
                )}

                <button
                    onClick={() => navigate('/productos-sura')}
                    className="w-full py-3 rounded-xl bg-gray-900 text-white font-medium flex items-center justify-center gap-2 hover:bg-gray-800 transition-colors"
                >
                    Ver detalles del pedido
                    <ArrowRight size={16} />
                </button>
            </div>
        </section>
    );
};

export default VentaEspecialWidget;
